import Avatar from 'boring-avatars'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Input } from '@/components/base/Input'
import { Modal } from '@/components/base/Modal'
import { Textarea } from '@/components/base/Textarea'

const MAX_TEAM_NAME_LENGTH = 10
const MAX_TEAM_DESCRIPTION_LENGTH = 30

interface TeamDetailFormModalProps {
  open: boolean
  onClose: () => void
  onBack: () => void
  onNext: (data: { name: string; description: string }) => void
}

export function TeamDetailFormModal({ open, onClose, onBack, onNext }: TeamDetailFormModalProps) {
  const { t } = useTranslation('team')
  const [teamName, setTeamName] = useState('')
  const [teamDescription, setTeamDescription] = useState('')

  // Validation checks
  const isTeamNameTooLong = teamName.length > MAX_TEAM_NAME_LENGTH
  const isTeamDescriptionTooLong = teamDescription.length > MAX_TEAM_DESCRIPTION_LENGTH
  const hasValidationError = isTeamNameTooLong || isTeamDescriptionTooLong

  const handleNext = () => {
    if (!teamName.trim()) {
      return
    }
    onNext({
      name: teamName,
      description: teamDescription,
    })
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[800px]">
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-3 hover:bg-panel-gray-200 rounded">
              <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
            </button>
            <span className="h6">
              <Trans ns="team" i18nKey="createTeam.step2Title">
                Team Description
              </Trans>
            </span>
            <span className="h6 text-tertiary">
              <Trans ns="team" i18nKey="createTeam.stepIndicator" values={{ current: 2, total: 4 }}>
                (2/4)
              </Trans>
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Team Avatar */}
          <div>
            <label className="block text-primary t-sm mb-2">{t('teamDetail.avatar')}</label>
            <div className="flex items-center gap-4">
              <Avatar
                size={80}
                name={teamName || 'Team'}
                variant="beam"
                colors={['#525866', '#7B8394', '#A0A8B8']}
              />
            </div>
          </div>

          {/* Team Name */}
          <div>
            <label className="block text-primary t-sm mb-2">{t('teamDetail.name')}</label>
            <Input
              placeholder={t('teamDetail.namePlaceholder')}
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
            />
            {isTeamNameTooLong && (
              <div className="text-error t-sm mt-1">
                {t('teamDetail.nameTooLong', {
                  current: teamName.length,
                  max: MAX_TEAM_NAME_LENGTH,
                })}
              </div>
            )}
          </div>

          {/* Team Description */}
          <div>
            <label className="block t-sm mb-2">{t('teamDetail.description')}</label>
            <Textarea
              placeholder={t('teamDetail.descriptionPlaceholder')}
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
              rows={4}
            />
            {isTeamDescriptionTooLong && (
              <div className="text-error t-sm mt-1">
                {t('teamDetail.descriptionTooLong', {
                  current: teamDescription.length,
                  max: MAX_TEAM_DESCRIPTION_LENGTH,
                })}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-4">
          <Button type="outline" className="min-w-[88px]" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="secondary"
            className="min-w-[88px]"
            onClick={handleNext}
            disabled={!teamName.trim() || hasValidationError}
          >
            {t('common.next')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
